import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  WritableSignal,
  OutputEmitterRef
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs/operators';
import { DateService } from '../../../services/date/date.service';
import { DateFormatterService } from '../../../services/date/date-formatter.service';
import { FocusDirective } from '../../focus/focus.directive';
import { DatePickerInterface } from '../date-picker/date-picker-interface';
import type { InputSignal, OnInit } from '@angular/core';
import type { DatePickerFormatType } from '../../../services/date/date-formatter.service';
import type { DateValidator } from '../date-picker/date-validator.type';
import type { DateComparator } from '../date-picker/date-comparator.type';
import type { DateObject } from './date-picker-inner.domain';

type ComparatorType = 'day' | 'month' | 'year' | 'dual-month-day';

interface StepType {
  years?: number,
  months?: number
}

/**
 * The Date Picker Inner Component
 *
 * This is the component that contains the logic for date picker
 */
@Component({
  selector: 'td-wb-date-picker-inner',
  templateUrl: './date-picker-inner.component.html',
  styleUrls: ['./date-picker-inner.component.scss'],
  imports: [FocusDirective]
})
export class DatePickerInnerComponent implements OnInit {
  /**
   * Prefix for datepicker id
   */
  private static readonly DATE_PICKER_ID_PREFIX: string = 'datepicker--';

  // ── DI ──────────────────────────────────────────────────────────────────
  private readonly dateFormatterService = inject(DateFormatterService);
  private readonly destroyRef = inject(DestroyRef);
  readonly datePicker: DatePickerInterface = inject(DatePickerInterface, { optional: true });
  readonly hostElement = inject(ElementRef);

  // ── Signal inputs ────────────────────────────────────────────────────────
  /**
   * Unique id set for each datepicker
   */
  readonly id: InputSignal<string | undefined> = input<string>();

  /**
   * Starting day of the week from 0-6 (0=Sunday, ..., 6=Saturday)
   */
  readonly startingDay: InputSignal<number> = input<number>(0);

  /**
   * Number of years to be displayed in the year selection
   */
  readonly yearRange: InputSignal<number> = input<number>(20);

  /**
   * Sets datepicker mode, supports: day, month, year, dual-month-day
   */
  readonly mode: InputSignal<string | undefined> = input<string>();

  /**
   * Sets minimum datepicker mode, supports: day, month, year
   */
  readonly minMode: InputSignal<string | undefined> = input<string>();

  /**
   * Sets maximum datepicker mode, supports: day, month, year
   */
  readonly maxMode: InputSignal<string | undefined> = input<string>();

  /**
   * if false week numbers will be hidden
   */
  readonly showWeeks: InputSignal<boolean> = input<boolean>(false);

  /**
   * Format to parse date entered by user
   */
  readonly format: InputSignal<string | undefined> = input<string>();

  /**
   * Format of day in month
   */
  readonly formatDay: InputSignal<DatePickerFormatType | undefined> = input<DatePickerFormatType>();

  /**
   * Format of month in year.
   */
  readonly formatMonth: InputSignal<DatePickerFormatType | undefined> = input<DatePickerFormatType>();

  /**
   * Format of year in year range.
   */
  readonly formatYear: InputSignal<DatePickerFormatType | undefined> = input<DatePickerFormatType>();

  /**
   * Format of day in week header.
   */
  readonly formatDayHeader: InputSignal<DatePickerFormatType | undefined> = input<DatePickerFormatType>();

  /**
   * Format of title when selecting day.
   */
  readonly formatDayTitle: InputSignal<DatePickerFormatType | undefined> = input<DatePickerFormatType>();

  /**
   * Format of title when selecting month.
   */
  readonly formatMonthTitle: InputSignal<DatePickerFormatType | undefined> = input<DatePickerFormatType>();

  /**
   * Function which should return true if date in the selector should be disabled for selection.
   */
  readonly disabledDates: InputSignal<DateValidator | undefined> = input<DateValidator>();

  /**
   * If true only dates of current month will be displayed.
   */
  readonly displayDatesOfCurrentMontyOnly: InputSignal<boolean> = input<boolean>(false);

  /**
   * Date selected from input
   */
  readonly parsedDate: InputSignal<Date | undefined> = input<Date>();

  /**
   * Latest selectable date
   */
  readonly maxDate: InputSignal<Date | undefined> = input<Date>();

  /**
   * Oldest selectable date
   */
  readonly minDate: InputSignal<Date | undefined> = input<Date>();

  // ── Output ───────────────────────────────────────────────────────────────
  /**
   * Event emitter for notifying date has been selected.
   */
  readonly selectionDone: OutputEmitterRef<Date> = output<Date>();

  // ── Internal mutable state ────────────────────────────────────────────────
  /**
   * Signal emitting true when the calendar should be focused.
   */
  readonly calendarFocus: WritableSignal<boolean> = signal(false);

  /**
   * Active date cell id (for aria-activedescendant). Exposed as a writable
   * signal so sub-picker templates that bind to it (`datePicker.activeDateId()`)
   * re-render whenever sub-pickers update the value via `updateActiveDateId()`
   * inside their `refreshView()` calls.
   */
  readonly activeDateId: WritableSignal<string> = signal('');

  /**
   * Step to move
   */
  step: Map<string, StepType> = new Map<string, StepType>();

  /**
   * Unique id set for each date picker
   */
  uniqueId: string;

  /**
   * Active date
   */
  activeDate: Date;

  /**
   * Allowed modes
   */
  modes: Array<string> = ['day', 'month', 'year', 'dual-month-day'];

  /**
   * Current mutable mode (used internally by sub-pickers for navigation)
   */
  currentMode: string;

  /**
   * Selected date
   */
  selectedDate: Date;

  /**
   * Function for displaying day view of date picker
   */
  refreshViewHandlerDay: () => void;

  /**
   * Function for comparing day
   */
  compareHandlerDay: DateComparator;

  /**
   * Function for displaying month view of date picker
   */
  refreshViewHandlerMonth: () => void;

  /**
   * Function for comparing month
   */
  compareHandlerMonth: DateComparator;

  /**
   * Function for displaying year view of date picker
   */
  refreshViewHandlerYear: () => void;

  /**
   * Function for comparing year
   */
  compareHandlerYear: DateComparator;

  /**
   * Function for displaying day view of dual month date picker
   */
  refreshViewHandlerDualMonthDay: () => void;

  private readonly dateBoundsState = computed(() => ({
    minDate: this.minDate(),
    maxDate: this.maxDate()
  }));

  private readonly parsedDate$ = toObservable(this.parsedDate);
  private readonly dateBounds$ = toObservable(this.dateBoundsState);

  constructor() {
    // NOTE: uniqueId is built from the host element's `id` attribute set by the
    // parent template via `[attr.id]="..."`. That binding has NOT been applied
    // yet during constructor execution, so we defer the assignment to ngOnInit.

    // ngOnInit handles initial input values; subscriptions handle subsequent updates.
    this.parsedDate$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((parsed: Date | undefined) => {
        if (DateService.isDate(parsed)) {
          this.activeDate = parsed;
          this.selectedDate = new Date(parsed.valueOf());
        } else {
          this.selectedDate = undefined;
        }
        this.refreshView();
      });

    this.dateBounds$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshView();
      });
  }

  ngOnInit(): void {
    // The parent's `[attr.id]` host binding has been applied by now, so it is
    // safe to read it and build the namespaced uniqueId used for cell ids and
    // aria-labelledby references.
    this.uniqueId = DatePickerInnerComponent.DATE_PICKER_ID_PREFIX + this.hostElement.nativeElement.getAttribute('id');

    // Signal inputs are available here — safe to read parsedDate(), mode() etc.
    // Sets activeDate and currentMode as defaults; the subscription above will
    // override activeDate/selectedDate once parsedDate() resolves (may be deferred via ngModel).
    if (DateService.isDate(this.parsedDate())) {
      this.activeDate = this.parsedDate();
      this.selectedDate = new Date(this.activeDate.valueOf());
    } else {
      this.activeDate = new Date();
    }
    this.currentMode = this.mode();
  }

  /**
   * This function sets compare handler
   *
   * @param comparator function for handling date comparison
   * @param comparatorType mode type
   */
  setCompareHandler(
    comparator: DateComparator,
    comparatorType: ComparatorType
  ): void {
    if (comparatorType === 'day') {
      this.compareHandlerDay = comparator;
    }
    if (comparatorType === 'month') {
      this.compareHandlerMonth = comparator;
    }
    if (comparatorType === 'year') {
      this.compareHandlerYear = comparator;
    }
    if (comparatorType === 'dual-month-day') {
      this.compareHandlerDay = comparator;
    }
  }

  /**
   * This function compares dates using compare handler
   *
   * @param date1 Date to compare
   * @param date2 Date to compare
   * @returns Result of comparison
   */
  compare(date1: Date, date2: Date): number | undefined {
    if ((date1 ?? null) === null || (date2 ?? null) === null) {
      return undefined;
    }
    if (this.currentMode === 'day' && this.compareHandlerDay !== undefined) {
      return this.compareHandlerDay(date1, date2);
    }
    if (this.currentMode === 'month' && this.compareHandlerMonth !== undefined) {
      return this.compareHandlerMonth(date1, date2);
    }
    if (this.currentMode === 'year' && this.compareHandlerYear !== undefined) {
      return this.compareHandlerYear(date1, date2);
    }
    if (this.currentMode === 'dual-month-day' && this.compareHandlerDay !== undefined) {
      return this.compareHandlerDay(date1, date2);
    }
    return undefined;
  }

  /**
   * This function sets refresh view handler
   *
   * @param handler function for displaying view
   * @param type mode type
   */
  setRefreshViewHandler(handler: () => void, type: string): void {
    if (type === 'day') {
      this.refreshViewHandlerDay = handler;
    } else if (type === 'month') {
      this.refreshViewHandlerMonth = handler;
    } else if (type === 'year') {
      this.refreshViewHandlerYear = handler;
    } else if (type === 'dual-month-day') {
      this.refreshViewHandlerDualMonthDay = handler;
    }
  }

  /**
   * This function calls the refresh view handler depending on the current mode
   */
  refreshView(): void {
    if (this.currentMode === 'day' && this.refreshViewHandlerDay !== undefined) {
      this.refreshViewHandlerDay();
    } else if (
      this.currentMode === 'month' &&
      this.refreshViewHandlerMonth !== undefined
    ) {
      this.refreshViewHandlerMonth();
    } else if (
      this.currentMode === 'year' &&
      this.refreshViewHandlerYear !== undefined
    ) {
      this.refreshViewHandlerYear();
    } else if (
      this.currentMode === 'dual-month-day' &&
      this.refreshViewHandlerDualMonthDay !== undefined
    ) {
      this.refreshViewHandlerDualMonthDay();
    }
  }

  /**
   * This function formats date
   *
   * @param date Date to format
   * @param format Date format
   * @returns Formatted date
   */
  formatDate(date: Date, format: DatePickerFormatType): string {
    return this.dateFormatterService.formatDate(date, format);
  }

  /**
   * This function checks if date is active
   *
   * @param dateObject Date to check for active
   * @returns Returns whether date is active
   */
  isActive(dateObject: DateObject): boolean {
    return this.compare(dateObject.date, this.activeDate) === 0;
  }

  createDateObject(date: Date, format: DatePickerFormatType): DateObject {
    const dateObject: DateObject = {} as unknown as DateObject;
    dateObject.date = new Date(date.valueOf());
    dateObject.label = this.formatDate(date, format);
    dateObject.selected = this.compare(date, this.selectedDate) === 0;
    dateObject.disabled = this.isDisabled(date);
    dateObject.current = this.compare(date, new Date()) === 0;
    return dateObject;
  }

  /**
   * Called by sub-pickers after building date objects to update the active descendant id.
   * Separated from isActive() to avoid side-effects in template bindings.
   */
  updateActiveDateId(dateObjects: Array<DateObject>): void {
    const active = dateObjects.find((d: DateObject) => this.isActive(d));
    if (active) {
      this.activeDateId.set(active.uid);
    }
  }

  /**
   * Split array into smaller arrays
   *
   * @param arr Array to split
   * @param size Size of new array
   * @returns Returns splitted array.
   */
  split(arr: Array<unknown>, size: number): Array<unknown> {
    const arrays: Array<unknown> = [];
    while (arr.length > 0) {
      arrays.push(arr.splice(0, size));
    }
    return arrays;
  }

  /**
   * This function handles selecting a date
   *
   * @param date Date to select
   * @param isManual True if date has been selected manually
   */
  select(date: Date, isManual: boolean = true): void {
    if (this.currentMode === this.minMode() || this.currentMode === 'dual-month-day') {
      if (this.activeDate === undefined || this.activeDate === null) {
        this.activeDate = new Date(0, 0, 0, 0, 0, 0, 0);
      }
      this.activeDate = new Date(date.valueOf());
      if (isManual) {
        this.selectionDone.emit(this.activeDate);
        if (this.datePicker !== null) {
          this.datePicker.focusInput();
        }
      }
    } else {
      this.activeDate = new Date(date.valueOf());
      if (isManual) {
        this.currentMode = this.modes[this.modes.indexOf(this.currentMode) - 1];
      }
      this.calendarFocus.set(true);
    }
    this.selectedDate = new Date(this.activeDate.valueOf());
    this.refreshView();
  }

  /**
   * This function moves date to previous/next
   *
   * @param direction Determines whether it should move previous or next
   */
  move(direction: number): void {
    const expectedStep: StepType = this.step.get(this.currentMode);
    if (expectedStep) {
      const yearStep: number =
        expectedStep.years === undefined ? 0 : expectedStep.years;
      const monthStep: number =
        expectedStep.months === undefined ? 0 : expectedStep.months;
      const year: number = this.activeDate.getFullYear() + direction * yearStep;
      const month: number = this.activeDate.getMonth() + direction * monthStep;
      this.activeDate = new Date(year, month, 1);
      this.refreshView();
    }
  }

  /**
   * This function toggles date picker's view mode (day, month, year)
   *
   * @param _direction Determines which mode to display
   */
  toggleMode(_direction: number): void {
    const direction = _direction === 0 ? 1 : _direction;
    if (
      !(this.currentMode === this.maxMode() && direction === 1) &&
      !(this.currentMode === this.minMode() && direction === -1)
    ) {
      this.currentMode = this.modes[this.modes.indexOf(this.currentMode) + direction];
      this.refreshView();
      this.calendarFocus.set(true);
    }
  }

  /**
   * This function returns whether a date is disabled
   *
   * @param date Date to check if it is disabled
   * @returns Returns true if the date is disabled.
   */
  protected isDisabled(date: Date): boolean {
    let disabled = false;
    const disabledDates = this.disabledDates();
    if ((this.currentMode === 'day' || this.currentMode === 'dual-month-day') && disabledDates !== undefined) {
      disabled = disabledDates(date);
    }
    return (
      disabled ||
      this.compare(date, this.minDate()) < 0 ||
      this.compare(date, this.maxDate()) > 0
    );
  }
}
